import { SensorData } from 'Simulations/SensorPoint';
import { FilterStage, StageParameters } from '../../Stage';
import type { StageContext } from '../../Context';
import { getSentence } from '@gui/constants/sentences';
import FVector from '@data/geometry/FVector';
import { NotificationType } from '@visualizer/types';

export type IndexStageParameters = StageParameters & {
    thresholdRange: [number, number];
    tolerance: number;
    subSortMode: number;
};

type Range = {
    start: number;
    end: number;
};
class IndexThresholdStage extends FilterStage<IndexStageParameters> {
    leftThresholdIndex: number = 0;
    rightThresholdIndex: number = Infinity;

    private iDIndexMap?: Map<number, number>;

    protected override processAll(context: StageContext): StageContext {
        this.iDIndexMap = this.computeIndexData(context.sensors);
        const outputSensors = context.sensors.map((sensorData) => this.process(sensorData, context)).filter((sd) => sd !== null);
        return { ...context, sensors: outputSensors };
    }

    protected filter(sensorData: SensorData): boolean {
        if (sensorData.getScalarValue() === 0) {
            if (this.getParams().thresholdRange[0] > 0) return false;
            else return true;
        }
        const actIndex = this.iDIndexMap!.get(sensorData.getID());

        const isActive = actIndex! >= this.leftThresholdIndex && actIndex! <= this.rightThresholdIndex;
        return isActive;
    }

    private computeIndexData(sensorData: SensorData[]): Map<number, number> {
        const iDIndexMap = new Map(sensorData.map((obj, index) => [obj.getID(), index]));
        const nonZeroIndices = sensorData.reduce<number[]>((acc, sd, index) => {
            if (sd.getScalarValue() !== 0) acc.push(index);
            return acc;
        }, []);

        if (nonZeroIndices.length === 0) {
            this.leftThresholdIndex = 0;
            this.rightThresholdIndex = sensorData.length - 1;
            return iDIndexMap;
        }

        const maxIndex = nonZeroIndices.length - 1;
        const leftNonZeroIndex = Math.ceil((this.getParams().thresholdRange[0] / 100) * maxIndex);
        const rightNonZeroIndex = Math.ceil((this.getParams().thresholdRange[1] / 100) * maxIndex);

        this.leftThresholdIndex = nonZeroIndices[leftNonZeroIndex];
        this.rightThresholdIndex = nonZeroIndices[rightNonZeroIndex];

        const tol = this.getParams().tolerance / 100;
        const leftRange = findSubarrayBounds(sensorData, this.leftThresholdIndex, tol);
        const rightRange = findSubarrayBounds(sensorData, this.rightThresholdIndex, tol);

        // Preprocessing input data
        const isEqualValues = leftRange.start != leftRange.end || rightRange.start != rightRange.end;
        if (isEqualValues) {
            // this.status.filter.warnMessage = 'Equal sensor points, decide how to sort';
            this.notify(NotificationType.Warning, getSentence('Filter.Sorting.Warning.Label'), 'subsort_criteria', false);
            this.subSort(sensorData, leftRange, rightRange, iDIndexMap);
        }
        return iDIndexMap;
    }

    private subSort(sensorData: SensorData[], leftRange: Range, rightRange: Range, idIndexMap: Map<number, number>): void {
        const previousFloor = this.getPreviousFloor().length != 0 ? this.getPreviousFloor() : sensorData;
        switch (this.getParams().subSortMode) {
            case 0: // no sort
                this.noSort(leftRange.start, rightRange.end);

                break;
            case 1: // random sort
                this.sortSubarrayRandomly(sensorData, idIndexMap, leftRange, rightRange);
                break;
            case 2: // Low sort
                this.sortSubarraysByActiveNeighbours(sensorData, idIndexMap, leftRange, rightRange, 0, previousFloor);
                break;
            case 3: // Low 1  sort
                this.sortSubarraysByActiveNeighbours(sensorData, idIndexMap, leftRange, rightRange, 1, previousFloor);
                break;
            case 4: // Barycentric sort
                this.sortSubarrayByDistance(sensorData, idIndexMap, leftRange, rightRange, previousFloor);

                break;
        }
    }

    private noSort(leftRangeStart: number, rightRangeEnd: number) {
        this.leftThresholdIndex = leftRangeStart;
        this.rightThresholdIndex = rightRangeEnd;
    }

    private sortSubarrayRandomly(sensorData: SensorData[], idIndexMap: Map<number, number>, leftRange: Range, rightRange: Range) {
        // Step 2: Define the random shuffle sort function
        const randomSort = () => Math.random() - 0.5; // This randomizes the order

        // Step 3: Sort and rotate if bounds are the same
        if ((leftRange.start === rightRange.start && leftRange.end === rightRange.end) || leftRange.end >= rightRange.start) {
            const unifiedBounds = { start: Math.min(leftRange.start, rightRange.start), end: Math.max(leftRange.end, rightRange.end) };

            sortAndRotateSubarray(sensorData, unifiedBounds, this.leftThresholdIndex, idIndexMap, true, randomSort); // Random sort and rotate
        }
        // Step 4: Sort and replace otherwise
        else {
            sortAndReplaceSubarray(sensorData, idIndexMap, leftRange, true, randomSort); // Sort first subarray randomly
            sortAndReplaceSubarray(sensorData, idIndexMap, rightRange, true, randomSort); // Sort second subarray randomly
        }
    }

    private sortSubarraysByActiveNeighbours(
        sensorData: SensorData[],
        idIndexMap: Map<number, number>,
        leftRange: Range,
        rightRange: Range,
        radius: number,
        lowestFloor: SensorData[]
    ) {
        // Precompute scores once
        const neighbourScores = new Map<number, number>();
        for (const sd of sensorData) {
            neighbourScores.set(sd.getID(), sd.computeNeighboursScore(radius, lowestFloor));
        }

        const neighboursSort = (a: SensorData, b: SensorData, ascending = true) => {
            const a_score = neighbourScores.get(a.getID());
            const b_score = neighbourScores.get(b.getID());
            return ascending ? (a_score ?? 0) - (b_score ?? 0) : (b_score ?? 0) - (a_score ?? 0);
        };

        if (leftRange.start == rightRange.start || leftRange.end == rightRange.end || leftRange.end >= rightRange.start) {
            const unifiedBounds = { start: Math.min(leftRange.start, rightRange.start), end: Math.max(leftRange.end, rightRange.end) };
            sortAndRotateSubarray(sensorData, unifiedBounds, this.leftThresholdIndex, idIndexMap, false, neighboursSort);
        } else {
            sortAndReplaceSubarray(sensorData, idIndexMap, leftRange, true, neighboursSort);
            sortAndReplaceSubarray(sensorData, idIndexMap, rightRange, false, neighboursSort);
        }
    }

    private sortSubarrayByDistance(
        sensorData: SensorData[],
        idIndexMap: Map<number, number>,
        leftRange: Range,
        rightRange: Range,
        lowestFloor: SensorData[]
    ) {
        const computeBarycenter = (subarray: SensorData[]): FVector => {
            const { x, y, z, size } = subarray.reduce(
                (acc, item) => {
                    const center = item.getPosition();

                    acc.x += center.x;
                    acc.y += center.y;
                    acc.z += center.z;
                    acc.size++;

                    return acc;
                },
                { x: 0, y: 0, z: 0, size: 0 }
            );
            return size > 0 ? new FVector(x, y, z).div(size) : new FVector();
        };

        const barycenter = computeBarycenter(lowestFloor);

        const barycenterSort = (a: SensorData, b: SensorData, ascending = true) =>
            ascending
                ? FVector.dist(a.getPosition(), barycenter) - FVector.dist(b.getPosition(), barycenter)
                : FVector.dist(b.getPosition(), barycenter) - FVector.dist(a.getPosition(), barycenter);

        if (leftRange.start == rightRange.start || leftRange.end == rightRange.end || leftRange.end >= rightRange.start) {
            // sortAndRotateSubarray(arr, boundsA, barycenter, indexA);
            const unifiedBounds = { start: Math.min(leftRange.start, rightRange.start), end: Math.max(leftRange.end, rightRange.end) };
            sortAndRotateSubarray(sensorData, unifiedBounds, this.leftThresholdIndex, idIndexMap, true, barycenterSort);
        } else {
            // Normal case: sort one subarray ascending, and the other descending
            sortAndReplaceSubarray(sensorData, idIndexMap, leftRange, false, barycenterSort);
            sortAndReplaceSubarray(sensorData, idIndexMap, rightRange, true, barycenterSort);
        }
    }
}

function findSubarrayBounds(sensorData: SensorData[], index: number, tol: number): { start: number; end: number } {
    const value = sensorData[index].getScalarValue();
    let start = index;
    let end = index;

    // Move backwards to find the start
    while (start > 0 && Math.abs(sensorData[start - 1].getScalarValue() - value) / value <= tol) {
        start--;
    }

    // Move forwards to find the end
    while (end < sensorData.length - 1 && Math.abs(sensorData[end + 1].getScalarValue() - value) / value <= tol) {
        end++;
    }

    return { start, end };
}

function sortAndRotateSubarray(
    arr: SensorData[],
    bounds: Range,
    indexA: number,
    indexMap: Map<number, number>,
    ascending: boolean,
    sortFunction: (a: SensorData, b: SensorData, ascending: boolean) => number
): void {
    // Step 1: Slice and sort the subarray in descending order by activeNeighbours
    const subarray = arr.slice(bounds.start, bounds.end + 1);
    subarray.sort((a, b) => sortFunction(a, b, ascending)); // Sort in descending order

    // Step 2: Calculate the number of steps needed to rotate
    const targetIndex = indexA - bounds.start; // Where we want the highest value to be (indexA)
    const rotateSteps = subarray.length - targetIndex; // Calculate rotation steps

    // Step 3: Perform circular rotation
    const rotatedSubarray = [
        ...subarray.slice(rotateSteps), // Elements from rotateSteps to the end
        ...subarray.slice(0, rotateSteps), // Elements from the start to rotateSteps-1
    ];

    // Step 4: Replace the original array with the rotated subarray
    for (let i = bounds.start; i <= bounds.end; i++) {
        arr[i] = rotatedSubarray[i - bounds.start];
        indexMap.set(arr[i].getID(), i); // Update index in map with new position
    }
}

const sortAndReplaceSubarray = (
    arr: SensorData[],
    indexMap: Map<number, number>,
    bounds: Range,
    ascending = true,
    sortFunction: (a: SensorData, b: SensorData, ascending: boolean) => number
) => {
    const subarray = arr.slice(bounds.start, bounds.end + 1);
    subarray.sort((a, b) => sortFunction(a, b, ascending));
    for (let i = bounds.start; i <= bounds.end; i++) {
        arr[i] = subarray[i - bounds.start];
        indexMap.set(arr[i].getID(), i); // Update index in map with new position
    }
};

export default IndexThresholdStage;
